
import { Component, Mixins, Prop } from 'vue-property-decorator';

import BasicButton from '@/ui-components/BasicButton.vue';

const MarkAsSeenModalGraphql = () => import('@/components/mark-as-seen/MarkAsSeenModalContainerGraphql.vue');

import { MarkAsSeenModalOptions, MarkAsSeenModalProps } from '@/components/mark-as-seen/MarkAsSeen';

import { AlertHelper } from '@/helpers/alert-helper';
import { ListMutationTitleDetailParam } from '@/helpers/providers/title-actions-provider';
import { captureMessageForSentry } from '@/helpers/sentry-helper';
import { ModalHelper } from '@/helpers/modal-helper';

import { QuickActionBarProps } from '@/interfaces/components/QuickActionBar';

import { ObjectType } from '@/@types/graphql-types';
import { TitleListName } from '@/interfaces/responses/title-list';

import TitleWatchlistMutationMixin from '@/mixins/title-quick-action-mutations/TitleWatchlistMutationMixin.vue';
import TitleSeenlistMutationMixin from '@/mixins/title-quick-action-mutations/TitleSeenlistMutationMixin.vue';
import TitleLikelistMutationMixin from '@/mixins/title-quick-action-mutations/TitleLikelistMutationMixin.vue';
import TitleDislikelistMutationMixin from '@/mixins/title-quick-action-mutations/TitleDislikelistMutationMixin.vue';
import { listsFeatureExperimentName, ListsFeatureExperimentVariantName } from '@/enums/experiments/lists-feature';
import { namespace } from 'vuex-class';
import { preventNonSignedInAction } from '@/helpers/prevent-non-signed-in-action-helper';

const LIST_TOGGLE_SOURCE = 'quick-action-bar';

const Experiment = namespace('experiment');

@Component({
	name: 'QuickActionBar',
	components: { BasicButton },
})
export default class QuickActionBar extends Mixins(
	TitleWatchlistMutationMixin,
	TitleSeenlistMutationMixin,
	TitleLikelistMutationMixin,
	TitleDislikelistMutationMixin
) {
	@Prop({ required: true }) titleObjectType: QuickActionBarProps['titleObjectType'];
	@Prop({ required: true }) titleObjectId: QuickActionBarProps['titleObjectId'];
	@Prop({ required: true }) titleId: QuickActionBarProps['titleId'];
	@Prop({ default: '' }) titleName: NonNullable<QuickActionBarProps['titleName']>;
	@Prop({ default: '' }) titlePosterUrl: QuickActionBarProps['titlePosterUrl'];
	@Prop() titleContentType: QuickActionBarProps['titleContentType'];
	@Prop({ default: () => [] }) titleBackdrops: QuickActionBarProps['titleBackdrops'];
	@Prop({ default: null }) titleSeenEpisodeCount: QuickActionBarProps['titleSeenEpisodeCount'];
	@Prop({ default: false }) isInWatchlist: NonNullable<QuickActionBarProps['isInWatchlist']>;
	@Prop({ default: false }) isInSeenList: NonNullable<QuickActionBarProps['isInSeenList']>;
	@Prop({ default: false }) isInLikeList: NonNullable<QuickActionBarProps['isInLikeList']>;
	@Prop({ default: false }) isInDislikeList: NonNullable<QuickActionBarProps['isInDislikeList']>;
	@Prop({ default: null }) seasonNumber: QuickActionBarProps['seasonNumber'];
	@Prop({ default: null }) episodeNumber: QuickActionBarProps['episodeNumber'];
	@Prop({ default: null }) seenEpisodeCount: QuickActionBarProps['seenEpisodeCount'];
	@Prop({ default: null }) showDetails: QuickActionBarProps['showDetails'];
	@Prop({ default: false }) isTitleUpcoming: NonNullable<QuickActionBarProps['isTitleUpcoming']>;
	@Prop({ default: true }) isTitleReleased: NonNullable<QuickActionBarProps['isTitleReleased']>;

	@Prop({ default: () => [] }) without: NonNullable<QuickActionBarProps['without']>;
	@Prop({ default: false }) fullWidth: QuickActionBarProps['fullWidth'];

	@Experiment.Getter activeVariants: Record<string, string>;

	/** Seen is shown only for experiment users and movies. TVST */
	get watchlistText() {
		if (this.titleObjectType === ObjectType.Movie) {
			return this.$t('WEBAPP_WATCHLIST');
		} else {
			return this.isInWatchlist ? this.$t('WEBAPP_TRACKING') : this.$t('WEBAPP_WATCHLIST_TV_SHOW_START_TRACKING');
		}
	}

	get seenButtonText() {
		const isSeenAll = this.titleObjectType === ObjectType.Show;
		if (isSeenAll) {
			return this.$t('WEBAPP_SEENLIST_SEENALL');
		}
		const seasonNumber = this.seasonNumber ? `S${this.seasonNumber} ` : '';
		return seasonNumber + this.$t('WEBAPP_SEENLIST_SEEN');
	}

	// Quick actions (except for seen) don't support Seasons
	get titleDetailsForMutation(): ListMutationTitleDetailParam {
		const titleDetails: ListMutationTitleDetailParam = {
			title: this.title,
			objectType: this.objectType,
			objectId: this.objectId,
			contentType: this.titleContentType,
		};

		if (this.seasonNumber) {
			titleDetails.seasonNumber = this.seasonNumber;
		}

		if (this.episodeNumber) {
			titleDetails.episodeNumber = this.episodeNumber;
		}

		return titleDetails;
	}

	get id() {
		return this.showDetails?.id ?? this.titleId;
	}

	get objectId() {
		return this.showDetails?.objectId ?? this.titleObjectId;
	}

	get objectType() {
		return this.showDetails?.objectType ?? this.titleObjectType;
	}

	get title() {
		return this.showDetails?.title ?? this.titleName;
	}

	get isListsFeatureVariant() {
		return this.activeVariants[listsFeatureExperimentName] === ListsFeatureExperimentVariantName.VARIANT;
	}

	get enableMarkAsSeen() {
		return this.isTitleReleased || this.titleObjectType === ObjectType.Movie;
	}

	get includeSeenList() {
		return this.without.includes(TitleListName.SEENLIST);
	}

	async toggleWatchlist() {
		const trackingPayload = {
			source: LIST_TOGGLE_SOURCE,
		};

		const updatedTitle = await this.mixin_setInWatchlist(
			this.id,
			this.isInWatchlist,
			this.titleDetailsForMutation,
			trackingPayload
		);

		if (updatedTitle) {
			const isInWatchlist = !!updatedTitle.data?.setInWatchlist.title.watchlistEntry?.createdAt;
			if (
				isInWatchlist &&
				(this.titleObjectType === ObjectType.Show || this.titleObjectType === ObjectType.ShowSeason) &&
				this.isTitleReleased
			) {
				ModalHelper.openModal(
					MarkAsSeenModalGraphql,
					{
						titleId: this.id,
						titleName: this.title,
						titleObjectId: this.objectId,
						titleObjectType: this.objectType,
						titleContentType: this.titleContentType,
						posterUrl: this.titlePosterUrl,
						backdrops: this.titleBackdrops,
						seenEpisodeCount: this.titleSeenEpisodeCount,
					} as MarkAsSeenModalProps,
					MarkAsSeenModalOptions,
					'',
					false,
					this.additionalContexts
				);
			}
		}
	}

	toggleLikelist() {
		const trackingPayload = {
			source: LIST_TOGGLE_SOURCE,
		};

		this.mixin_setInLikelist(
			this.id,
			this.isInLikeList,
			this.isInDislikeList,
			this.titleDetailsForMutation,
			trackingPayload
		);
	}

	toggleDislikelist() {
		const trackingPayload = {
			source: LIST_TOGGLE_SOURCE,
		};

		this.mixin_setInDislikelist(
			this.id,
			this.isInDislikeList,
			this.isInLikeList,
			this.titleDetailsForMutation,
			trackingPayload
		);
	}

	async toggleSeenlist() {
		const preventAction = await preventNonSignedInAction();

		if (preventAction) {
			return;
		}

		try {
			const setInSeenList = () =>
				this.mixin_setInSeenlist(
					this.titleId,
					this.isInSeenList,
					{
						// Not using titleDetailsForMutation here, because we can mark Season as seen
						title: this.titleName,
						objectId: this.titleObjectId,
						contentType: this.titleContentType,
						objectType: this.titleObjectType,
						seasonNumber: this.seasonNumber,
						episodeNumber: this.episodeNumber,
						showObjectId: this.showDetails?.objectId,
					},
					undefined,
					{
						source: LIST_TOGGLE_SOURCE,
					}
				);

			if (this.titleObjectType === ObjectType.Show) {
				AlertHelper.showAlert({
					header: (!this.isInSeenList
						? this.$t('WEBAPP_SEENALL_TITLE_ADD')
						: this.$t('WEBAPP_SEENALL_TITLE_REMOVE')) as string,
					message: this.$t('WEBAPP_SEENALL_MESSAGE') as string,
					buttons: [this.$t('WEBAPP_SEENALL_NO') as string, this.$t('WEBAPP_SEENALL_YES') as string],
					additionalContexts: this.additionalContexts,
				}).then(clickResult => {
					clickResult && setInSeenList();
				});
			} else {
				setInSeenList();
			}
		} catch (error) {
			const err: any = error;
			captureMessageForSentry(`Problems on toggling a season or tv show: ${err.message}`, {
				where: 'QuickActionBar',
			});
			throw new Error(err.message);
		}
	}
}
